
import React from 'react';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { ArrowBackRounded as BackIcon } from '@mui/icons-material';

export interface PageTitleProps {
  /** Page heading — rendered with the theme's `h1` page-title variant. */
  title: string;
  /** Optional one-line description under the title (tertiary tone). */
  subtitle?: string;
  /** Optional inline element next to the title (e.g. a status/filter chip). */
  adornment?: React.ReactNode;
  /** Optional action(s) at the right of the title row, for a page that is not a record but still
   *  has one thing to do (a record page takes `RecordHeader`). Wraps under the title when narrow. */
  actions?: React.ReactNode;
  /** The list a detail or create page belongs to. Draws a ghost back button before the title —
   *  the way back on a phone, where the breadcrumb shows only its last crumb. */
  backTo?: string;
}

/**
 * The single, theme-driven page title used by every screen (Fleet is the
 * reference look). Typography comes from the theme — the `h1` page-title
 * variant plus the tertiary micro-tone for the subtitle — so titles never
 * drift between screens (previously h1/h5/h6/CardHeader were all mixed).
 * Never restyle a page title locally; render it through this component.
 */
export const PageTitle: React.FC<PageTitleProps> = ({ title, subtitle, adornment, actions, backTo }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();

  const text = (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h1">{title}</Typography>
        {adornment}
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: theme.custom.text.tertiary }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const heading = backTo ? (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
      <Tooltip title={translate('ra.action.back')}>
        <IconButton onClick={() => navigate(backTo)} aria-label={translate('ra.action.back')} sx={{ ml: -1, mt: -0.5 }}>
          <BackIcon />
        </IconButton>
      </Tooltip>
      {text}
    </Box>
  ) : text;

  if (!actions) return heading;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, minWidth: 0 }}>
      {heading}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>
    </Box>
  );
};

export default PageTitle;
